import React, { useState } from "react";
import { motion } from "framer-motion";

import image_welcome_self from "./images/Kuhuk_photo_plaza.jpg";
import wandrobe_logo from "./images/wandrobe-logo.png";
import { ReactComponent as CodeIcon } from "./images/code-2.svg";
import { ReactComponent as TelescopeIcon } from "./images/telescope.svg";
import { ReactComponent as PenLineIcon } from "./images/pen-line.svg";

const highlights = [
  { number: "7+ yrs", label: "professional software engineering" },
  { number: "16M+", label: "downloads across shipped apps" },
  { number: "1", label: "startup, solo-founded — Wandrobe" },
];

const Hero = () => {
  return (
    <div className="hero">
      <motion.h1
        className="hero-title"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        Kuhuk Sharma 👩🏻‍💻
      </motion.h1>

      <div className="hero-row">
        <motion.div
          className="hero-left"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <p className="hero-subtitle">
            Founding Engineer, building{" "}
            <a href="https://wandrobe.com/" target="_blank" rel="noreferrer" className="hero-subtitle-link">
              Wandrobe
            </a>{" "}
            solo — full-stack, mobile & infra — and a lifelong stargazer 🔭
          </p>

          <p className="hero-blurb">
            I'm a software engineer with seven years of professional experience —
            and I'm now pouring that into Wandrobe, a startup I'm building entirely
            solo: backend, frontend, cross-platform mobile, infrastructure, all of
            it. Outside of code, I'm chasing the next clear night sky with a
            telescope.
          </p>

          <div className="hero-highlights">
            {highlights.map((h) => (
              <div className="hero-highlight" key={h.label}>
                <span className="hero-highlight-number">{h.number}</span>
                <span className="hero-highlight-label">{h.label}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.img
          src={image_welcome_self}
          alt="Kuhuk"
          className="hero-image"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        />
      </div>
    </div>
  );
};

const Story = () => {
  return (
    <div className="story">
      <h2 className="section-title">how I got here 🌱</h2>
      <p className="story-text">
        I spent seven years as a professional software engineer, shipping apps that
        reach tens of millions of people — breaking news, job search, entertainment.
        Somewhere along the way, a much smaller, much more personal problem kept
        nagging at me: I like being put together every day, but I could never keep
        track of what I owned, what actually worked together, or what I'd already
        worn twice that week.
      </p>
      <p className="story-text">
        That itch eventually became{" "}
        <a href="https://wandrobe.com/" target="_blank" rel="noreferrer" className="hero-subtitle-link">
          Wandrobe
        </a>{" "}
        — an AI stylist I'm building solo, end to end, as a Founding Engineer:
        backend, frontend, mobile, and infrastructure, all of it. Outside of code,
        I'm looking up — telescope out, chasing whatever's visible that night.
      </p>
    </div>
  );
};

const HowIWork = () => {
  return (
    <div className="how-i-work">
      <h2 className="section-title">how I work ⚡</h2>
      <p className="how-i-work-text">
        Curious by default. I ship fast, and I build with AI as part of my
        process, not a shortcut around it.
      </p>
      <a href="/contact_me" className="hero-contact-link">let's talk →</a>
    </div>
  );
};

const Card = ({ Icon, image, title, link, external, iconClassName }) => {
  return (
    <motion.a
      href={link}
      className="universe-card"
      whileHover={{ y: -10, scale: 1.05 }}
      {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    >
      <div className="universe-card-icon-slot">
        {image ? (
          <img src={image} alt={title} className="universe-card-image" />
        ) : (
          <Icon className={`universe-card-icon${iconClassName ? ` ${iconClassName}` : ""}`} />
        )}
      </div>
      <p className="universe-card-title">{title}</p>
    </motion.a>
  );
};

const Universe = () => {
  return (
    <div className="universe">
      <h2 className="section-title">poke around ✨</h2>
      <p className="section-subtitle">a few doors into what I do — pick one</p>

      <div className="universe-card-container">
        <Card Icon={CodeIcon} title="coding" link="/coding" />
        <Card image={wandrobe_logo} title="wandrobe" link="https://wandrobe.com/" external />
        <Card Icon={TelescopeIcon} title="stargazing" link="/astronomy" />
        <Card Icon={PenLineIcon} title="writing" link="/blog" iconClassName="universe-card-icon-pen" />
      </div>
    </div>
  );
};

const featuredProjects = [
  {
    logo: wandrobe_logo,
    title: "Wandrobe",
    stat: "Founder & Engineer",
    features: [
      "AI-powered wardrobe cataloging",
      "Daily outfit + weather suggestions",
      "Trip planning & packing lists",
      "Solo-built: backend, frontend, mobile, infra",
    ],
    accent: "#B5502D",
    link: "https://wandrobe.com/",
    linkLabel: "Visit Wandrobe →",
    external: true,
  },
  {
    logo: "https://play-lh.googleusercontent.com/CGZDUrECYSxqYBZ4QmvOlhOyZ4EgHw6F31zJLJvYognAqEUtA80eqvQZ5i5i5hVmTFc=w480-h960-rw",
    title: "News18 Android App",
    stat: "5M+ downloads",
    features: [
      "Bottom navigation",
      "Ads integration",
      "GA4 integration",
      "Video shorts & live TV",
      "Built in Jetpack Compose",
    ],
    accent: "#1E3A5F",
    link: "/coding",
    linkLabel: "See more work →",
    external: false,
  },
  {
    logo: "https://play-lh.googleusercontent.com/8t6U6HGuMnP1DAJYpb4U_fEwVA7fgaOBJYRyfPHM5OLZllGj-8tsmJhu6Y4ikMrGpZg=w480-h960-rw",
    title: "Internshala Android App",
    stat: "5M+ downloads",
    features: [
      "Owned job search, chat & notifications; migrated to Android 13",
      "Maintained technical docs, code reviews & Git pipelines",
      "Partnered with backend, design, product & business teams",
    ],
    accent: "#6B3F5E",
    link: "/coding",
    linkLabel: "See more work →",
    external: false,
  },
];

const FlipCard = ({ logo, title, stat, features, accent, link, linkLabel, external }) => {
  const [flipped, setFlipped] = useState(false);

  return (
    <div
      className={`flip-card${flipped ? " is-flipped" : ""}`}
      onClick={() => setFlipped((f) => !f)}
    >
      <div className="flip-card-inner">
        <div className="flip-card-front" style={{ background: accent }}>
          <img src={logo} alt={title} className="flip-card-logo" />
          <h3 className="flip-card-title">{title}</h3>
          <p className="flip-card-stat">{stat}</p>
        </div>

        <div className="flip-card-back">
          <h3 className="flip-card-back-title">{title}</h3>
          <ul className="flip-card-features">
            {features.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
          <a
            href={link}
            onClick={(e) => e.stopPropagation()}
            {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
            className="flip-card-link"
          >
            {linkLabel}
          </a>
        </div>
      </div>
    </div>
  );
};

const Featured = () => {
  return (
    <div className="featured">
      <h2 className="section-title">some things I've shipped 🚀</h2>
      <p className="section-subtitle">a peek at the work — more on the Work page</p>

      <div className="flip-card-container">
        {featuredProjects.map((p) => (
          <FlipCard key={p.title} {...p} />
        ))}
      </div>

      <a href="/coding" className="pill-button pill-button-primary featured-cta">
        see the full lineup →
      </a>
    </div>
  );
};

const Cta = () => {
  return (
    <div className="cta-section">
      <h2 className="section-title">say hi 👋</h2>
      <p className="section-subtitle cta-subtitle">got a project, a role, or just want to talk astronomy?</p>
      <a href="/contact_me" className="pill-button pill-button-primary">Get in touch</a>
    </div>
  );
};

const Kuhuk = () => {
  return (
    <div className="page">
      <Hero />
      <Story />
      <HowIWork />
      <Universe />
      <Featured />
      <Cta />
    </div>
  );
};

export default Kuhuk;
